import { inject, Injectable } from '@angular/core';
import { Action, createSelector, State, StateContext } from '@ngxs/store';
import { firstValueFrom } from 'rxjs';
import { Page } from '../../../shared/model/local/pagination/page';
import { RegionApiService } from '../service/region.api.service';
import { RegionProductPageCriteria } from '../model/region-product-page-criteria';
import { Product } from '../model/product.model';
import { RegionActions } from './region.actions';

export interface RegionStateModel {
  productsPage: Page<Product>;
  criteria: RegionProductPageCriteria;
  distributorId: number;
}

@State<RegionStateModel>({
  name: 'region',
  defaults: {
    productsPage: null,
    criteria: null,
    distributorId: null,
  },
})
@Injectable()
export class RegionState {
  private readonly regionApiService: RegionApiService =
    inject(RegionApiService);

  static productsPage() {
    return createSelector([RegionState], (state: RegionStateModel) => {
      return state.productsPage;
    });
  }

  static criteria() {
    return createSelector([RegionState], (state: RegionStateModel) => {
      return state.criteria;
    });
  }

  @Action(RegionActions.GetPage, { cancelUncompleted: true })
  async getContactRequestPage(
    ctx: StateContext<RegionStateModel>,
    action: RegionActions.GetPage
  ) {
    try {
      const productsPage = await firstValueFrom(
        this.regionApiService.getProductsPage(
          action.distributorId,
          action.criteria
        )
      );

      ctx.patchState({
        productsPage,
        criteria: action.criteria,
        distributorId: action.distributorId,
      });

      return Promise.resolve();
    } catch (ex) {
      return Promise.reject(ex);
    }
  }

  @Action(RegionActions.RefreshPage)
  async refreshPage(ctx: StateContext<RegionStateModel>) {
    try {
      const state = ctx.getState();

      const productsPage = await firstValueFrom(
        this.regionApiService.getProductsPage(
          state.distributorId,
          state.criteria
        )
      );

      ctx.patchState({
        productsPage,
      });

      return Promise.resolve();
    } catch (ex) {
      return Promise.reject(ex);
    }
  }

  @Action(RegionActions.ResetState)
  async resetState(ctx: StateContext<RegionStateModel>) {
    try {
      ctx.patchState({
        productsPage: null,
        criteria: null,
      });
      return Promise.resolve();
    } catch (ex) {
      return Promise.reject(ex);
    }
  }
}
